import { getTenantTodayIso } from './date'

/** Matches donor appointments page + portal eligibility APIs */
export const PERMANENT_ELIGIBILITY_DEFERRAL_DATE = '9999-09-09'

export const isPermanentEligibilityDeferral = nextEligibilityDate =>
  nextEligibilityDate === PERMANENT_ELIGIBILITY_DEFERRAL_DATE ||
  String(nextEligibilityDate || '').startsWith(
    `${PERMANENT_ELIGIBILITY_DEFERRAL_DATE}T`
  )

export const normalizeProcedureTypeId = value =>
  value === undefined || value === null || value === ''
    ? ''
    : String(value).trim()

/** Active catalog row from `/procedure-types` — id field varies by serializer */
export const catalogProcedureTypeId = pt => {
  if (!pt || typeof pt !== 'object') return ''
  return normalizeProcedureTypeId(pt.id ?? pt.procedure_type_id)
}

export const findProcedureEligibilityRow = (
  procedureEligibilities,
  procedureTypeId
) => {
  const pid = normalizeProcedureTypeId(procedureTypeId)
  if (!pid || !Array.isArray(procedureEligibilities)) return null
  return (
    procedureEligibilities.find(
      e => normalizeProcedureTypeId(e?.procedure_type_id) === pid
    ) ?? null
  )
}

/** yyyy-mm-dd (or ISO with time) → local calendar Date at noon for pickers */
export const parseIsoDateToLocalNoon = isoDateStr => {
  const part = String(isoDateStr || '').split('T')[0]
  const [y, m, d] = part.split('-').map(Number)
  if (!y || !m || !d || Number.isNaN(y)) return null
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

const startOfLocalDay = date => {
  const x = new Date(date)
  x.setHours(0, 0, 0, 0)
  return x
}

/**
 * Next calendar day to use when a donor picks a procedure (logged-in `/auth/me`
 * procedure_eligibilities). Null means do not auto-fill (no row / eligible anytime /
 * deferred). Dates before tenant “today” are clamped to today.
 *
 * @param {Array<{ procedure_type_id?: string|number, next_eligibility_date?: string|null }>|undefined} procedureEligibilities
 * @param {string|number|null|undefined} procedureTypeId
 * @returns {Date|null}
 */
export const getSuggestedDonationDateFromProcedureEligibility = (
  procedureEligibilities,
  procedureTypeId
) => {
  const pid =
    procedureTypeId == null || procedureTypeId === ''
      ? ''
      : String(procedureTypeId)
  if (!pid || !Array.isArray(procedureEligibilities)) return null

  const row = findProcedureEligibilityRow(
    procedureEligibilities,
    procedureTypeId
  )
  const raw = row?.next_eligibility_date
  if (raw == null || raw === '') return null

  if (isPermanentEligibilityDeferral(raw)) {
    return null
  }

  const parsed = parseIsoDateToLocalNoon(raw)
  if (!parsed || Number.isNaN(parsed.getTime())) return null

  const tenantTodayIso = getTenantTodayIso()
  const [ty, tm, td] = tenantTodayIso.split('-').map(Number)
  const tenantStart = new Date(ty, tm - 1, td, 0, 0, 0, 0)
  const parsedStart = startOfLocalDay(parsed)

  if (parsedStart < tenantStart) {
    return parseIsoDateToLocalNoon(tenantTodayIso)
  }
  return parsed
}

/**
 * Earliest suggested donation date across all procedure eligibility rows for a donor.
 * Used when no specific procedure is selected.
 */
export const getEarliestSuggestedDonationDateFromEligibilities =
  procedureEligibilities => {
    if (!Array.isArray(procedureEligibilities)) return null
    let earliest = null
    for (const row of procedureEligibilities) {
      const suggested = getSuggestedDonationDateFromProcedureEligibility(
        procedureEligibilities,
        row?.procedure_type_id
      )
      if (!suggested) continue
      if (!earliest || suggested.getTime() < earliest.getTime()) {
        earliest = suggested
      }
    }
    return earliest
  }

/**
 * @param {string|null|undefined} nextEligibilityDate - ISO date yyyy-mm-dd or null
 * @returns {{ status: 'eligible'|'not_eligible'|'deferred', label: string }}
 */
export const getProcedureEligibilityStatus = nextEligibilityDate => {
  if (isPermanentEligibilityDeferral(nextEligibilityDate)) {
    return { status: 'deferred', label: 'Deferred' }
  }

  if (nextEligibilityDate == null || nextEligibilityDate === '') {
    return { status: 'eligible', label: 'Eligible' }
  }

  const date = new Date(
    String(nextEligibilityDate).includes('T')
      ? nextEligibilityDate
      : `${nextEligibilityDate}T12:00:00`
  )
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)

  if (date <= today) {
    return { status: 'eligible', label: 'Eligible' }
  }

  return { status: 'not_eligible', label: 'Not eligible' }
}

/**
 * Eligibility to donate on a specific calendar day (matches donor-portal
 * isDonorEligibleForProcedure with operationDate). Deferred donors are treated
 * as not eligible for UI (same as calendar product rules).
 *
 * @param {string|null|undefined} nextEligibilityDate
 * @param {string} appointmentDayIso - yyyy-mm-dd (session day in calendar)
 * @returns {{ status: 'eligible'|'not_eligible', label: string }}
 */
export const getProcedureEligibilityStatusForAppointmentDate = (
  nextEligibilityDate,
  appointmentDayIso
) => {
  if (isPermanentEligibilityDeferral(nextEligibilityDate)) {
    return { status: 'not_eligible', label: 'Not eligible' }
  }

  if (nextEligibilityDate == null || nextEligibilityDate === '') {
    return { status: 'eligible', label: 'Eligible' }
  }

  const nextStr = String(nextEligibilityDate).split('T')[0]
  const dayStr = String(appointmentDayIso || '').split('T')[0]

  if (dayStr >= nextStr) {
    return { status: 'eligible', label: 'Eligible' }
  }

  return { status: 'not_eligible', label: 'Not eligible' }
}

/**
 * True if this procedure could be booked on at least one day in the month whose
 * last calendar day is monthEndIso (yyyy-mm-dd).
 * @deprecated Prefer {@link procedureHasBookableDayInDateRange} with explicit month bounds.
 */
export const procedureHasBookableDayInMonthByMonthEnd = (
  nextEligibilityDate,
  monthEndIso
) => {
  if (isPermanentEligibilityDeferral(nextEligibilityDate)) return false
  if (nextEligibilityDate == null || nextEligibilityDate === '') return true
  const nextStr = String(nextEligibilityDate).split('T')[0]
  const mEnd = String(monthEndIso || '').split('T')[0]
  return nextStr <= mEnd
}

/**
 * True if the donor could be eligible on at least one calendar day in
 * [rangeStartIso, rangeEndIso] (yyyy-mm-dd, inclusive).
 * Missing eligibility row or null next date ⇒ eligible anytime ⇒ true.
 * Permanent deferral ⇒ false. First eligible day after range end ⇒ false.
 */
export const procedureHasBookableDayInDateRange = (
  nextEligibilityDate,
  rangeStartIso,
  rangeEndIso
) => {
  if (isPermanentEligibilityDeferral(nextEligibilityDate)) return false
  if (nextEligibilityDate == null || nextEligibilityDate === '') return true
  const nextStr = String(nextEligibilityDate).split('T')[0]
  const end = String(rangeEndIso || '').split('T')[0]
  const start = String(rangeStartIso || '').split('T')[0]
  if (!end || !start || start > end) return true
  if (nextStr > end) return false
  return true
}

/**
 * Unique procedure types offered on schedule rows (zip / drive / session API),
 * in a shape compatible with {@link catalogProcedureTypeId}.
 */
export const getProcedureCatalogFromScheduleRows = scheduleRows => {
  const map = new Map()
  for (const row of scheduleRows || []) {
    let arr = row?.procedure_types
    if (typeof arr === 'string') {
      try {
        arr = JSON.parse(arr)
      } catch {
        arr = null
      }
    }
    if (!Array.isArray(arr)) continue
    for (const p of arr) {
      const id = catalogProcedureTypeId(p)
      if (!id || map.has(id)) continue
      map.set(id, {
        id,
        name: p.procedure_type_name ?? p.name ?? '',
      })
    }
  }
  return Array.from(map.values())
}

/**
 * Logged-in donor, no procedure filter: block scheduling only when **every**
 * procedure in `procedureCatalog` has a known eligibility outcome with **no**
 * bookable day in [rangeStartIso, rangeEndIso]. Procedures with **no** eligibility
 * row are treated as potentially eligible (keep UI open).
 */
export const donorCannotBookAnyProcedureForCatalog = ({
  donorId,
  procedureEligibilities,
  procedureCatalog,
  rangeStartIso,
  rangeEndIso,
}) => {
  if (
    !donorId ||
    !Array.isArray(procedureCatalog) ||
    procedureCatalog.length === 0
  ) {
    return false
  }

  if (!Array.isArray(procedureEligibilities)) return false

  for (const pt of procedureCatalog) {
    const id = catalogProcedureTypeId(pt)
    if (!id) continue
    const row = findProcedureEligibilityRow(procedureEligibilities, id)
    const next = row?.next_eligibility_date
    if (procedureHasBookableDayInDateRange(next, rangeStartIso, rangeEndIso)) {
      return false
    }
  }

  return true
}

/**
 * Donor-center calendar: full `procedureTypes` catalog + visible month date range.
 *
 * @param {{ donorId?: string|number, procedureEligibilities?: unknown, procedureCatalog?: Array<Record<string, unknown>>, monthStartIso: string, monthEndIso: string }} args
 */
export const donorCannotBookAnyProcedureThisMonth = ({
  donorId,
  procedureEligibilities,
  procedureCatalog,
  monthStartIso,
  monthEndIso,
}) =>
  donorCannotBookAnyProcedureForCatalog({
    donorId,
    procedureEligibilities,
    procedureCatalog,
    rangeStartIso: monthStartIso,
    rangeEndIso: monthEndIso,
  })

/** @param {unknown} raw - API `procedure_types` JSON string or array */
export const parseProcedureTypesOnScheduleRow = raw => {
  let arr = raw
  if (typeof arr === 'string') {
    try {
      arr = JSON.parse(arr)
    } catch {
      return []
    }
  }
  return Array.isArray(arr) ? arr : []
}

/**
 * Single source of truth for donor eligibility on a schedule result row (donor center,
 * zip search, future flows). Guests: no eligibility UI and scheduling is not gated here.
 *
 * @param {object} args
 * @param {string|number|undefined|null} args.donorId
 * @param {unknown} args.procedureEligibilities - donor.procedure_eligibilities
 * @param {string|number|undefined|null} args.selectedProcedureTypeId - filter; empty = use row procedures
 * @param {string} args.appointmentDayIso - yyyy-mm-dd
 * @param {unknown} args.procedureTypesOnRow - raw row `procedure_types`
 * @returns {{ applies: boolean, selectable: boolean, chip: { label: string, status: 'eligible'|'not_eligible' } | null }}
 */
export const getDonorEligibilityForScheduleRow = ({
  donorId,
  procedureEligibilities,
  selectedProcedureTypeId,
  appointmentDayIso,
  procedureTypesOnRow,
}) => {
  if (!donorId) {
    return { applies: false, selectable: true, chip: null }
  }

  const dayStr = String(appointmentDayIso || '').split('T')[0]
  const selected = normalizeProcedureTypeId(selectedProcedureTypeId)

  if (selected) {
    const row = findProcedureEligibilityRow(procedureEligibilities, selected)
    if (!row) {
      return {
        applies: true,
        selectable: false,
        chip: { label: 'Not eligible', status: 'not_eligible' },
      }
    }
    const st = getProcedureEligibilityStatusForAppointmentDate(
      row.next_eligibility_date,
      dayStr
    )
    const selectable = st.status === 'eligible'
    return {
      applies: true,
      selectable,
      chip: {
        label: st.label,
        status: selectable ? 'eligible' : 'not_eligible',
      },
    }
  }

  const arr = parseProcedureTypesOnScheduleRow(procedureTypesOnRow)
  if (arr.length === 0) {
    return {
      applies: true,
      selectable: false,
      chip: { label: 'Not eligible', status: 'not_eligible' },
    }
  }

  for (const p of arr) {
    const id = catalogProcedureTypeId(p)
    if (!id) continue
    const er = findProcedureEligibilityRow(procedureEligibilities, id)
    if (!er) continue
    const st = getProcedureEligibilityStatusForAppointmentDate(
      er.next_eligibility_date,
      dayStr
    )
    if (st.status === 'eligible') {
      return {
        applies: true,
        selectable: true,
        chip: { label: 'Eligible', status: 'eligible' },
      }
    }
  }

  return {
    applies: true,
    selectable: false,
    chip: { label: 'Not eligible', status: 'not_eligible' },
  }
}

/**
 * Eligibility chip for donor-center calendar day cells and mobile day banner.
 * With a selected procedure: same rules as {@link getDonorEligibilityForScheduleRow}.
 * Without a procedure: eligible when any session row on the day is bookable for the donor.
 */
export const getDonorEligibilityForCalendarDay = ({
  donorId,
  procedureEligibilities,
  selectedProcedureTypeId,
  appointmentDayIso,
  sessionRows,
}) => {
  const selected = normalizeProcedureTypeId(selectedProcedureTypeId)

  if (selected) {
    return getDonorEligibilityForScheduleRow({
      donorId,
      procedureEligibilities,
      selectedProcedureTypeId: selected,
      appointmentDayIso,
      procedureTypesOnRow: sessionRows?.[0]?.procedure_types,
    })
  }

  if (!donorId) {
    return { applies: false, selectable: true, chip: null }
  }

  const rows = Array.isArray(sessionRows) ? sessionRows : []
  if (rows.length === 0) {
    return { applies: false, selectable: true, chip: null }
  }

  let anyOpenSession = false
  for (const row of rows) {
    const slots = Number(row?.available_slots_count)
    if (Number.isNaN(slots) || slots <= 0) continue
    anyOpenSession = true
    const result = getDonorEligibilityForScheduleRow({
      donorId,
      procedureEligibilities,
      selectedProcedureTypeId: '',
      appointmentDayIso,
      procedureTypesOnRow: row?.procedure_types,
    })
    if (result.selectable) {
      return {
        applies: true,
        selectable: true,
        chip: { label: 'Eligible', status: 'eligible' },
      }
    }
  }

  if (!anyOpenSession) {
    return { applies: false, selectable: true, chip: null }
  }

  return {
    applies: true,
    selectable: false,
    chip: { label: 'Not eligible', status: 'not_eligible' },
  }
}

/** Result-card / session-chip eligibility from a schedule row (zip, city, sponsor, donor center). */
export const getScheduleRowEligibilityChip = args => {
  const elig = getDonorEligibilityForScheduleRow(args)
  return elig.applies ? elig.chip : null
}

/** @deprecated Use {@link getDonorEligibilityForScheduleRow} */
export const scheduleRowSelectableForDonorEligibility = args =>
  getDonorEligibilityForScheduleRow({
    donorId: args.donorId,
    procedureEligibilities: args.procedureEligibilities,
    selectedProcedureTypeId: args.selectedProcedureTypeId,
    appointmentDayIso: args.appointmentDayIso,
    procedureTypesOnRow: args.procedureTypesOnRow,
  }).selectable
